<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PrimaryButton } from '@es-bill/es-bill-common-components';
import { billRunAnalysisKey, usebillrunsStore } from '@/stores/billrunsStore';
import { formatBillingMasterDataComments } from '@/services/billrunFiltersService';
import { useCommunitiesStore } from '@/stores/communitiesStore';
import { formatDate, formatDateTime } from '@/lib/es-bill-shared-js/misc/timeTools';

const billrunsStore = usebillrunsStore();
const communitiesStore = useCommunitiesStore();
const { t } = useI18n();

const expandedCommunityEcids = ref(new Set());
const statusFilter = ref('all');
const selectedCommunityEcids = ref([]);

const communityNameByEcid = computed(() => {
  const map = new Map();
  for (const c of communitiesStore.allCommunities) {
    if (c.ecid) {
      map.set(c.ecid, c.name);
    }
  }
  return map;
});

const displayBillRuns = computed(() => {
  const analysisByKey = billrunsStore.analysisByBillRunKey;
  const interpretationByKey = billrunsStore.interpretationByBillRunKey;

  return billrunsStore.billruns.map((br) => {
    const key = billRunAnalysisKey(br.ecid, br.runIdentifier);
    const analysis = analysisByKey[key] ?? null;
    const interpretation = interpretationByKey[key];
    const dateExecuted = analysis?.dateExecuted ? new Date(analysis.dateExecuted) : null;
    const isBillable = interpretation?.isBillable ?? false;
    const showSumDeviationBadge = interpretation?.showSumDeviationBadge ?? false;
    const displayStatus = br.testRun
      ? 'testRun'
      : isBillable
        ? 'billable'
        : showSumDeviationBadge
          ? 'sumDeviation'
          : 'notBillable';
    return {
      ...br,
      communityName: communityNameByEcid.value.get(br.ecid) ?? br.ecid,
      billingPeriodDisplay: `${formatDate(new Date(br.billingPeriodFrom))} – ${formatDate(new Date(br.billingPeriodTo))}`,
      hasAnalysisResult: analysis != null,
      analysisDateExecutedDisplay: dateExecuted ? formatDateTime(dateExecuted) : null,
      sumDeviationDisplay: interpretation?.sumDeviationDisplay ?? '—',
      hasSumDeviation: interpretation?.hasSumDeviation ?? false,
      incorrectQhCount: interpretation?.incorrectQhCount ?? 0,
      l3Count: interpretation?.l3Count ?? 0,
      surplusCount: interpretation?.surplusCount ?? 0,
      missingDataCount: interpretation?.missingDataCount ?? 0,
      displayStatus,
      commentsDisplay: formatBillingMasterDataComments(br.comments),
    };
  });
});

function groupBillRunsByCommunity(billruns) {
  const byEcid = new Map();
  for (const br of billruns) {
    if (!byEcid.has(br.ecid)) {
      byEcid.set(br.ecid, {
        ecid: br.ecid,
        ecn: br.ecn,
        communityName: br.communityName,
        billruns: [],
      });
    }
    byEcid.get(br.ecid).billruns.push(br);
  }
  for (const group of byEcid.values()) {
    group.billruns.sort((a, b) => {
      if (a.testRun !== b.testRun) {
        return a.testRun ? -1 : 1;
      }
      return new Date(a.billingPeriodTo) - new Date(b.billingPeriodTo);
    });
  }
  return [...byEcid.values()].sort((a, b) =>
    String(a.ecn ?? '').localeCompare(String(b.ecn ?? ''), undefined, { numeric: true })
  );
}

const filteredBillRuns = computed(() => {
  if (statusFilter.value === 'all') {
    return displayBillRuns.value;
  }
  return displayBillRuns.value.filter(
    (br) => br.displayStatus === statusFilter.value
  );
});

const resultCommunityOptions = computed(() =>
  groupBillRunsByCommunity(displayBillRuns.value).map((group) => ({
    title: group.communityName,
    value: group.ecid,
    ecn: group.ecn,
  }))
);

const groupedCommunities = computed(() => {
  const groups = groupBillRunsByCommunity(filteredBillRuns.value);
  if (selectedCommunityEcids.value.length === 0) {
    return groups;
  }
  const selectedEcids = new Set(selectedCommunityEcids.value);
  return groups.filter((group) => selectedEcids.has(group.ecid));
});

const allCommunityEcids = computed(
  () => new Set(groupedCommunities.value.map((group) => group.ecid))
);

const isDataFullyLoaded = computed(
  () =>
    billrunsStore.hasFetched
    && !billrunsStore.loading
    && !billrunsStore.analysisLoading
    && displayBillRuns.value.length > 0
);

const statusFilterOptions = computed(() => [
  { title: t('all'), value: 'all' },
  { title: t('billrunsDashboard.testRun'), value: 'testRun' },
  { title: t('billrunsDashboard.billable'), value: 'billable' },
  { title: t('billrunsDashboard.columnSumDeviation'), value: 'sumDeviation' },
  { title: t('billrunsDashboard.notBillable'), value: 'notBillable' },
]);

function isCommunityExpanded(ecid) {
  return expandedCommunityEcids.value.has(ecid);
}

function toggleCommunity(ecid) {
  const next = new Set(expandedCommunityEcids.value);
  if (next.has(ecid)) {
    next.delete(ecid);
  } else {
    next.add(ecid);
  }
  expandedCommunityEcids.value = next;
}

function expandAllCommunities() {
  expandedCommunityEcids.value = new Set(allCommunityEcids.value);
}

function collapseAllCommunities() {
  expandedCommunityEcids.value = new Set();
}

const communitiesExpandMode = computed({
  get() {
    const allExpanded =
      allCommunityEcids.value.size > 0
      && expandedCommunityEcids.value.size === allCommunityEcids.value.size;
    return allExpanded ? 'expanded' : 'collapsed';
  },
  set(value) {
    if (value === 'expanded') {
      expandAllCommunities();
    } else {
      collapseAllCommunities();
    }
  },
});

const billingRelevantFilterMode = computed({
  get() {
    return billrunsStore.billingRelevantMeterCodesOnly ? 'billingRelevant' : 'all';
  },
  set(value) {
    billrunsStore.setBillingRelevantMeterCodesOnly(value === 'billingRelevant');
  },
});

watch(
  () => billrunsStore.billruns,
  (billruns) => {
    statusFilter.value = 'all';
    selectedCommunityEcids.value = [];
    if (billruns.length === 0) {
      expandedCommunityEcids.value = new Set();
      return;
    }
    expandAllCommunities();
  }
);

watch(resultCommunityOptions, (options) => {
  const availableEcids = new Set(options.map((option) => option.value));
  selectedCommunityEcids.value = selectedCommunityEcids.value.filter((ecid) =>
    availableEcids.has(ecid)
  );
});

async function onGetData() {
  try {
    await billrunsStore.fetchAllBillingMasterData();
  } catch {
    // error stored in billrunsStore.error
  }
}

function copyRunIdentifier(runIdentifier) {
  navigator.clipboard.writeText(runIdentifier);
}
</script>

<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">
      <h1 class="text-h4 mb-6">{{ $t('billrunsDashboard.dashboardTitle') }}</h1>

      <v-card class="mb-4" elevation="2">
        <v-card-title>
          <v-icon class="mr-2" color="primary">mdi-cog</v-icon>
          {{ $t('setup') }}
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-wrap align-center ga-4">
            <v-text-field
              v-model.number="billrunsStore.offsetDays"
              :label="$t('billrunsDashboard.offsetDays')"
              type="number"
              min="0"
              variant="outlined"
              bg-color="white"
              class="offset-days-field"
              hide-details
            />
            <div class="d-flex align-center ga-2">
              <span class="text-body-2 text-medium-emphasis">
                {{ $t('billrunsDashboard.billingRelevantMeterCodes') }}
                <v-tooltip activator="parent" location="top">
                  <span style="white-space: pre-line">
                    {{ $t('billrunsDashboard.billingRelevantMeterCodesTooltip') }}
                  </span>
                </v-tooltip>
              </span>
              <v-btn-toggle
                v-model="billingRelevantFilterMode"
                mandatory
                divided
                density="compact"
                color="primary"
              >
                <v-btn value="billingRelevant" size="small">
                  {{ $t('billrunsDashboard.billingRelevantFilterRelevant') }}
                </v-btn>
                <v-btn value="all" size="small">{{ $t('all') }}</v-btn>
              </v-btn-toggle>
            </div>
          </div>
          <v-row>
            <v-col cols="12" sm="4" md="3">
              <PrimaryButton
                :text="$t('billrunsDashboard.getData')"
                :loading="billrunsStore.loading"
                @click="onGetData"
              />
            </v-col>
          </v-row>
          <v-row v-if="isDataFullyLoaded" align="center" class="mt-2 ga-4">
            <v-col cols="12" sm="auto" class="d-flex align-center flex-wrap ga-2">
              <span class="text-body-2 text-medium-emphasis">
                {{ $t('billrunsDashboard.details') }}
              </span>
              <v-btn-toggle
                v-model="communitiesExpandMode"
                mandatory
                divided
                density="compact"
                color="primary"
              >
                <v-btn value="expanded" size="small">{{ $t('all') }}</v-btn>
                <v-btn value="collapsed" size="small">{{ $t('none') }}</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="12" sm="auto" class="d-flex align-center flex-wrap ga-2">
              <v-select
                v-model="statusFilter"
                :items="statusFilterOptions"
                :label="$t('billrunsDashboard.columnStatus')"
                variant="outlined"
                density="compact"
                hide-details
                bg-color="white"
                class="status-filter-field"
              />
            </v-col>
            <v-col cols="12" sm="auto" class="d-flex align-center flex-wrap ga-2">
              <v-autocomplete
                v-model="selectedCommunityEcids"
                :items="resultCommunityOptions"
                :label="$t('billrunsDashboard.communities')"
                :filter-keys="['title', 'value', 'ecn']"
                item-title="title"
                item-value="value"
                multiple
                chips
                closable-chips
                clearable
                variant="outlined"
                density="compact"
                hide-details
                bg-color="white"
                class="community-filter-field"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card elevation="2">
        <v-card-title>
          <v-icon class="mr-2" color="primary">mdi-table</v-icon>
          {{ $t('billrunsDashboard.billrunsResults') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="billrunsStore.error"
            type="error"
            variant="tonal"
            class="mb-4"
          >
            {{ billrunsStore.error }}
          </v-alert>

          <v-alert
            v-if="billrunsStore.analysisError"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            {{ billrunsStore.analysisError }}
          </v-alert>

          <p
            v-if="!billrunsStore.hasFetched && !billrunsStore.loading"
            class="text-medium-emphasis"
          >
            {{ $t('billrunsDashboard.pressGetDataToLoad') }}
          </p>

          <p
            v-else-if="billrunsStore.hasFetched && !billrunsStore.loading && displayBillRuns.length === 0"
            class="text-medium-emphasis"
          >
            {{ $t('billrunsDashboard.noBillrunsFound') }}
          </p>

          <div
            v-else-if="billrunsStore.hasFetched && (billrunsStore.loading || billrunsStore.analysisLoading)"
          >
            <v-progress-linear
              indeterminate
              color="primary"
              class="mb-4"
            />
          </div>

          <div
            v-else-if="billrunsStore.hasFetched && !billrunsStore.analysisLoading && displayBillRuns.length > 0"
          >
            <p
              v-if="groupedCommunities.length === 0 && statusFilter !== 'all'"
              class="text-medium-emphasis"
            >
              {{ $t('billrunsDashboard.noBillrunsMatchStatusFilter') }}
            </p>
            <p
              v-else-if="groupedCommunities.length === 0 && selectedCommunityEcids.length > 0"
              class="text-medium-emphasis"
            >
              {{ $t('billrunsDashboard.noBillrunsFound') }}
            </p>
            <v-card
              v-for="group in groupedCommunities"
              :key="group.ecid"
              class="mb-4"
              elevation="1"
            >
              <v-card-title
                class="d-flex align-center py-3 community-card-title"
                @click="toggleCommunity(group.ecid)"
              >
                <div class="flex-grow-1">
                  <div class="text-subtitle-1 font-weight-medium">{{ group.communityName }}</div>
                  <div class="text-body-2 text-medium-emphasis">
                    {{ $t('ecn') }}: {{ group.ecn }} · {{ $t('ecid') }}: {{ group.ecid }}
                  </div>
                </div>
                <v-btn
                  icon
                  size="small"
                  @click.stop="toggleCommunity(group.ecid)"
                >
                  <v-icon>{{ isCommunityExpanded(group.ecid) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </v-card-title>
              <v-expand-transition>
                <div v-show="isCommunityExpanded(group.ecid)" class="px-4 pb-4">
                  <v-table density="compact" class="billruns-table">
                    <thead>
                      <tr>
                        <th>{{ $t('billrunsDashboard.columnStatus') }}</th>
                        <th>{{ $t('billrunsDashboard.runIdentifier') }}</th>
                        <th>{{ $t('billrunsDashboard.columnBillingPeriod') }}</th>
                        <th>{{ $t('billrunsDashboard.columnLastDataAnalysis') }}</th>
                        <th>{{ $t('billrunsDashboard.columnSumDeviation') }}</th>
                        <th class="text-end">{{ $t('billrunsDashboard.columnIncorrectQh') }}</th>
                        <th class="text-end">{{ $t('billrunsDashboard.columnL3') }}</th>
                        <th class="text-end">{{ $t('billrunsDashboard.columnSurplus') }}</th>
                        <th class="text-end">{{ $t('billrunsDashboard.columnMissingData') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template
                        v-for="br in group.billruns"
                        :key="`${group.ecid}-${br.runIdentifier}`"
                      >
                        <tr :class="{ 'billrun-row-with-comments': br.commentsDisplay }">
                          <td>
                            <div class="billrun-status-chips">
                              <v-chip
                                v-if="br.displayStatus === 'testRun'"
                                size="x-small"
                                variant="elevated"
                                color="warning"
                              >
                                {{ $t('billrunsDashboard.testRun') }}
                              </v-chip>
                              <v-chip
                                v-if="br.displayStatus === 'billable'"
                                size="x-small"
                                variant="tonal"
                                color="success"
                              >
                                {{ $t('billrunsDashboard.billable') }}
                              </v-chip>
                              <v-chip
                                v-if="br.displayStatus === 'sumDeviation'"
                                size="x-small"
                                variant="tonal"
                                color="info"
                              >
                                {{ $t('billrunsDashboard.columnSumDeviation') }}
                                <v-tooltip activator="parent" location="top">
                                  <span style="white-space: pre-line">
                                    {{ $t('billrunsDashboard.sumDeviationTooltip') }}
                                  </span>
                                </v-tooltip>
                              </v-chip>
                              <v-chip
                                v-if="br.displayStatus === 'notBillable'"
                                size="x-small"
                                variant="tonal"
                                color="error"
                              >
                                {{ $t('billrunsDashboard.notBillable') }}
                              </v-chip>
                            </div>
                          </td>
                          <td>
                            <div class="d-flex align-center">
                              <span>{{ br.runIdentifier }}</span>
                              <v-btn
                                icon
                                size="x-small"
                                variant="text"
                                class="ml-1"
                                :title="t('clickToCopy')"
                                @click="copyRunIdentifier(br.runIdentifier)"
                              >
                                <v-icon size="small">mdi-content-copy</v-icon>
                              </v-btn>
                            </div>
                          </td>
                          <td>{{ br.billingPeriodDisplay }}</td>
                          <td>
                            <template v-if="br.hasAnalysisResult">
                              {{ br.analysisDateExecutedDisplay }}
                            </template>
                            <template v-else>
                              {{ $t('billrunsDashboard.noAnalysisResult') }}
                            </template>
                          </td>
                          <td :class="{ 'metric-sum-deviation': br.hasSumDeviation }">
                            {{ br.sumDeviationDisplay }}
                          </td>
                          <td
                            class="text-end"
                            :class="{ 'metric-incorrect-qh': br.incorrectQhCount !== 0 }"
                          >
                            {{ br.incorrectQhCount }}
                          </td>
                          <td class="text-end" :class="{ 'metric-l3': br.l3Count !== 0 }">
                            {{ br.l3Count }}
                          </td>
                          <td class="text-end" :class="{ 'metric-surplus': br.surplusCount !== 0 }">
                            {{ br.surplusCount }}
                          </td>
                          <td
                            class="text-end"
                            :class="{ 'metric-missing': br.missingDataCount !== 0 }"
                          >
                            {{ br.missingDataCount }}
                          </td>
                        </tr>
                        <tr v-if="br.commentsDisplay" class="billrun-comments-row">
                          <td colspan="9" class="metric-missing billrun-comments-cell">
                            {{ br.commentsDisplay }}
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </v-table>
                </div>
              </v-expand-transition>
            </v-card>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-container>
</template>

<style scoped>
.offset-days-field {
  flex: 0 0 140px;
  max-width: 140px;
}

.status-filter-field {
  flex: 0 0 180px;
  min-width: 180px;
  max-width: 220px;
}

.community-filter-field {
  flex: 1 1 240px;
  min-width: 240px;
  max-width: 360px;
}

.community-card-title {
  cursor: pointer;
}

.billruns-table :deep(th) {
  white-space: nowrap;
}

.billrun-status-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.metric-sum-deviation {
  background-color: #e8bf9073;
}

.metric-incorrect-qh {
  background-color: #ede0f5;
}

.metric-l3 {
  background-color: #ffd6d6;
}

.metric-surplus {
  background-color: #d4f5f0;
}

.metric-missing {
  background-color: #fff0d6;
}

.billruns-table :deep(tr.billrun-row-with-comments > td) {
  border-bottom: none;
}

.billruns-table :deep(tr.billrun-comments-row > td) {
  border-top: none;
  padding-top: 0;
}

.billrun-comments-cell {
  white-space: pre-line;
}
</style>
